import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import * as data from '../../data/prototypeData'
import ChestReveal from './ChestReveal'
import { usePrototype } from './PrototypeContext'
import { toPlace } from '../../rules/progression/hive'
import '../v2/WarResultModal.css'
import './prototype.css'

// ─── The war's end (progression prototype) ───────────────────────────────────
// What the war paid, from the server's settlement (views/{seat}/reveals/end,
// progression/warEnd.settleWarEnd's receipt), played in order: the result,
// the survival reward counting up, unspent yield turned into raw resources,
// the ascension to a new arena, then Open War Chest: each chest opens on the
// chest screen (ChestReveal), the next after it, and the last goes home, or
// to the Hive when something the chests brought waits to be placed.

const HEADLINE = { win: 'Victory', loss: 'Loss', draw: 'Draw' }
const fmt = (n) => Math.floor(n ?? 0).toLocaleString('en-US')
const entries = (o) => Object.entries(o ?? {}).filter(([, n]) => n > 0)

function reasonText(result, seat) {
    if (!result || result.reason === 'kills') return null
    const mine = result.loser === seat
    if (result.reason === 'clock') return mine ? 'Your clock ran out.' : 'Their clock ran out.'
    if (result.reason === 'missed turns') return mine ? 'You missed too many turns.' : 'They missed too many turns.'
    return mine ? 'You forfeited.' : 'They forfeited.'
}

// Counts from 0 to `to` over `ms`.
function useCountUp(to, ms = 900) {
    const [n, setN] = useState(0)
    const raf = useRef(null)
    useEffect(() => {
        if (!to) { setN(0); return undefined }
        const t0 = performance.now()
        const tick = (t) => {
            const k = Math.min(1, (t - t0) / ms)
            setN(Math.round(to * (1 - (1 - k) ** 3)))
            if (k < 1) raf.current = requestAnimationFrame(tick)
        }
        raf.current = requestAnimationFrame(tick)
        return () => cancelAnimationFrame(raf.current)
    }, [to, ms])
    return n
}

export default function WarEnd({ warId, seat, result, warDoc, onHome }) {
    const [end, setEnd] = useState(null)
    const [opened, setOpened] = useState(0)          // chests opened so far
    const [opening, setOpening] = useState(false)    // the next chest's opening is up
    const { player, openHive } = usePrototype()
    const toHive = !!player && toPlace(player).count > 0
    useEffect(() => {
        let alive = true
        let tries = 0
        const load = () => data.loadReveal(warId, seat, 'end').then(r => {
            if (!alive) return
            if (r) setEnd(r)
            else if (++tries < 10) setTimeout(load, 800)
        }).catch(e => console.error('War end failed to load', e))
        load()
        return () => { alive = false }
    }, [warId, seat])

    const receipt = end?.receipt
    const bucks = useCountUp(receipt?.survivalBucks ?? 0)
    const chests = receipt?.chests ?? []
    const reason = reasonText(end?.result ?? warDoc?.result, seat)

    // The card's middle sits on the board's middle (the band between the two
    // Hives), not the war screen's: the overlay centres it, then it's shifted
    // by the difference. Re-measured when the window or the card resizes.
    const overlayRef = useRef(null)
    const cardRef = useRef(null)
    const [shift, setShift] = useState(0)
    useLayoutEffect(() => {
        const measure = () => {
            const board = document.querySelector('[data-testid=hex-board-surface]')
            const overlay = overlayRef.current
            if (!board || !overlay) return
            const b = board.getBoundingClientRect(), o = overlay.getBoundingClientRect()
            setShift(Math.round((b.top + b.height / 2) - (o.top + o.height / 2)))
        }
        measure()
        window.addEventListener('resize', measure)
        return () => window.removeEventListener('resize', measure)
    }, [])

    return (
        <div ref={overlayRef} className={`wrm__overlay wrm__overlay--${result}`}>
            <div ref={cardRef} className="wrm__card pt-end" style={{ transform: `translateY(${shift}px)` }}>
                <h1 className="wrm__headline">{HEADLINE[result] ?? 'Draw'}</h1>
                {reason && <p className="wrm__subtext">{reason}</p>}

                {!receipt ? <p className="pt-dim">Counting up…</p> : receipt.testWar ? (
                    // A test war on generated Hives pays nothing.
                    <p className="pt-dim">A test war on generated Hives: no rewards.</p>
                ) : (
                    <>
                        <div className="pt-end__row">
                            <span>Survival reward</span>
                            <strong>{fmt(bucks)} bucks</strong>
                        </div>
                        {end.outcome?.clockForfeit && <p className="pt-dim">No survival reward after running out of clock.</p>}

                        {entries(receipt.convertedRaw).length > 0 && (
                            <div className="pt-end__row">
                                <span>Yield left over</span>
                                <strong>{entries(receipt.convertedRaw).map(([r, n]) => `${fmt(n)} ${r}`).join(', ')}</strong>
                            </div>
                        )}

                        {receipt.ascended && (
                            <div className="pt-end__ascend">
                                <span>Your Hive ascends</span>
                                <strong>Arena {receipt.ascended.from} → Arena {receipt.ascended.to}</strong>
                            </div>
                        )}

                    </>
                )}

                {opening && chests[opened] && (
                    // Done: the next chest's screen; after the last, the Hive
                    // when there's something to place, else home.
                    <ChestReveal key={opened} receipt={chests[opened]}
                        doneLabel={opened + 1 >= chests.length && toHive ? 'Place on Hive' : 'Done'}
                        onDone={() => {
                            if (opened + 1 < chests.length) setOpened(n => n + 1)
                            else if (toHive) openHive()
                            else onHome()
                        }} />
                )}

                {/* The way on is the chests: each opens on its own screen, the
                    next after it, then home. A war with none goes home. */}
                {receipt && (chests.length > 0 ? (
                    <button className="wrm__cta" onClick={() => setOpening(true)}>
                        <span className="wrm__cta-main">Open War Chest</span>
                    </button>
                ) : (
                    <button className="wrm__cta" onClick={onHome}>
                        <span className="wrm__cta-main">Home</span>
                    </button>
                ))}
            </div>
        </div>
    )
}
